/**
 * The honesty badge (MEASURED / MODELED / SAMPLE / ESTIMATED), decided ONLY from the
 * flags the API sent. Nothing here looks at a number, a name or a page to guess what a
 * figure rests on. A figure that carries no flag gets no badge (null), and the console
 * marks that in development so the missing flag gets fixed in the API, not papered over.
 */
import type { Basis } from './api/types';

export type HonestyLabel = 'MEASURED' | 'MODELED' | 'SAMPLE' | 'ESTIMATED';

/** Every flag shape the back end uses today on a response or on a single figure. */
export interface HonestyFlags {
  /** Response-level: true = real data, false = sample. */
  real?: boolean | null;
  /** Per-figure basis. */
  basis?: Basis | string | null;
  /**
   * Per-figure: part or all of it is modeled (a catalog rate standing in for a claim).
   * Response-level, a list of the field names that are modeled (the server's `modeled: [...]`
   * convention): the response mixes measured and modeled figures, so `real: true` alone
   * no longer vouches for an unflagged figure in it.
   */
  modeled?: boolean | readonly string[] | null;
  /** Per-figure: an estimate. */
  estimated?: boolean | null;
  /** Per-figure: sample or illustrative data. */
  sample?: boolean | null;
  /** Per-figure: measured from recorded events. */
  measured?: boolean | null;
}

const BY_BASIS: Record<string, HonestyLabel> = {
  measured: 'MEASURED',
  verified: 'MEASURED',
  live: 'MEASURED',
  modeled: 'MODELED',
  modelled: 'MODELED',
  sample: 'SAMPLE',
  estimated: 'ESTIMATED',
  estimate: 'ESTIMATED',
};

/**
 * The badge for one figure, or null when no flag says. Precedence, strongest first:
 * a sample response (real: false) makes every figure in it SAMPLE; then the figure's own
 * basis; then its modeled / estimated / sample / measured booleans; then a real response
 * (real: true) makes it MEASURED, unless a layer lists modeled fields (a mixed response),
 * where an unflagged figure gets no badge rather than a MEASURED it may not deserve.
 */
export function honestyOf(...layers: (HonestyFlags | null | undefined)[]): HonestyLabel | null {
  const present = layers.filter((l): l is HonestyFlags => !!l && typeof l === 'object');
  if (present.some((l) => l.real === false)) return 'SAMPLE';
  // The innermost layer (the figure itself) is last; read from the inside out.
  for (let i = present.length - 1; i >= 0; i--) {
    const l = present[i];
    if (typeof l.basis === 'string' && BY_BASIS[l.basis.toLowerCase()]) return BY_BASIS[l.basis.toLowerCase()];
    if (l.sample === true) return 'SAMPLE';
    if (l.modeled === true) return 'MODELED';
    if (l.estimated === true) return 'ESTIMATED';
    if (l.measured === true) return 'MEASURED';
  }
  // A real response vouches for an unflagged figure only when it does not say it mixes
  // in modeled figures: inside a mixed response each figure must carry its own flag.
  const mixed = present.some((l) => Array.isArray(l.modeled) && l.modeled.length > 0);
  if (!mixed && present.some((l) => l.real === true)) return 'MEASURED';
  return null;
}

/**
 * The back end's Measure type (src/core/measure.ts) carries its flag as
 * provenance.method; its `basis` field is a human sentence, not a flag. Map the method:
 * measured, reported (a figure someone recorded, e.g. CMS) and derived (arithmetic on
 * recorded figures) read MEASURED; adjusted reads ESTIMATED; modeled reads MODELED.
 */
export type MeasureMethod = 'measured' | 'reported' | 'adjusted' | 'modeled' | 'derived';

export function flagsFromMeasure(m: { provenance?: { method?: string | null } | null } | null | undefined): HonestyFlags | null {
  const method = m?.provenance?.method;
  if (method === 'measured' || method === 'reported' || method === 'derived') return { basis: 'measured' };
  if (method === 'adjusted') return { basis: 'estimated' };
  if (method === 'modeled') return { basis: 'modeled' };
  return null;
}

/**
 * The tag to draw for a figure: its honesty label, except SAMPLE inside a seeded building
 * scope (the server says so: /auth/me demo_building), which shows no banner and no "demo"
 * on screen (Andrew 2026-10-09). Every other
 * label (MEASURED, MODELED, ESTIMATED) is drawn as always, and outside a demo building
 * scope (a real building, a fleet view) SAMPLE is drawn too.
 */
export function shownHonesty<L extends string>(label: L | null, demoBuilding: boolean): L | null {
  return demoBuilding && label === 'SAMPLE' ? null : label;
}

/** True when at least one layer carries a flag honestyOf can read. */
export function hasHonestyFlag(...layers: (HonestyFlags | null | undefined)[]): boolean {
  return honestyOf(...layers) !== null;
}

/**
 * Build labels (SAMPLE, MODELED, MODELED EST., WHAT-IF, ESTIMATED, MEASURED, NOT LOGGED YET,
 * "trend MODELED") are for the people building and checking Aithora: Developer and Super
 * Admin see them; every customer role does not (Andrew, V6 lock, 2026-10-12). The one rule;
 * the console reads it through useBuildLabels, never per page.
 */
export function showsBuildLabels(role: string | null | undefined): boolean {
  return role === 'DEVELOPER' || role === 'SUPER_ADMIN';
}

/**
 * What a customer sees in place of a build label (Andrew, 2026-10-15): a modeled or
 * estimated number carries a small, quiet "est." right after it; MEASURED shows nothing;
 * SAMPLE, NOT LOGGED YET and every other build tag never show. Developer and Super Admin
 * keep the full label.
 */
export const EST_MARK = 'est.';
const ESTIMATE_WORD = /^(MODELED EST\.|MODELED|MODEL|ESTIMATED|EST\.|PART MODEL)$/i;
export function isEstimateWord(word: string | null | undefined): boolean {
  return !!word && ESTIMATE_WORD.test(word.trim());
}
export function customerMark(word: string | null | undefined): typeof EST_MARK | null {
  return isEstimateWord(word) ? EST_MARK : null;
}
